/**
 * TradingSessionProvider - 交易时段上下文
 * 
 * 提供统一的交易时段信息：
 * - 是否交易日
 * - 是否交易时段
 * - 推荐刷新间隔
 */

import { createContext, useContext, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api/client';
import type { TradingSessionInfo } from './types';

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

interface TradingSessionContextValue {
  session: TradingSessionInfo;
  isLoading: boolean;
  refresh: () => void;
}

const defaultSession: TradingSessionInfo = {
  isTradingDay: false,
  isTradingHours: false,
  sessionType: 'closed',
  statusText: '休市',
  currentDate: formatLocalDate(new Date()),
  currentTime: new Date().toTimeString().split(' ')[0],
  nextOpen: null,
  refreshInterval: 30000,
  cacheTTL: 60,
  lastTradingDay: formatLocalDate(new Date()),
  dataDate: formatLocalDate(new Date()),
};

const TradingSessionContext = createContext<TradingSessionContextValue | null>(null);

export function TradingSessionProvider({ children }: { children: ReactNode }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['trading-session'],
    queryFn: async () => {
      try {
        const { data: response } = await api.get('/unified/trading-session');
        const data = response.data ?? response;
        return {
          isTradingDay: data.is_trading_day,
          isTradingHours: data.is_trading_hours,
          sessionType: data.session_type,
          statusText: data.status_text,
          currentDate: data.current_date,
          currentTime: data.current_time,
          nextOpen: data.next_open,
          refreshInterval: data.refresh_interval * 1000, // 转换为毫秒
          cacheTTL: data.cache_ttl,
          lastTradingDay: data.last_trading_day || data.current_date,
          dataDate: data.data_date || data.current_date,
        } as TradingSessionInfo;
      } catch (error) {
        console.warn('Failed to fetch trading session:', error);
        return getLocalTradingSession();
      }
    },
    refetchInterval: 60000, // 每分钟刷新一次
    staleTime: 30000,
  });

  const session = data ?? defaultSession;

  return (
    <TradingSessionContext.Provider
      value={{
        session,
        isLoading,
        refresh: refetch,
      }}
    >
      {children}
    </TradingSessionContext.Provider>
  );
}

export function useTradingSession(): TradingSessionContextValue {
  const context = useContext(TradingSessionContext);
  if (!context) {
    // 如果没有Provider，返回本地计算的值
    return {
      session: getLocalTradingSession(),
      isLoading: false,
      refresh: () => {},
    };
  }
  return context;
}

/**
 * 本地计算交易时段（备用方案）
 */
function getLocalTradingSession(): TradingSessionInfo {
  const now = new Date();
  const weekday = now.getDay();
  const hours = now.getHours();
  const minutes = now.getMinutes();
  const currentMinutes = hours * 60 + minutes;

  // 工作日判断
  const isTradingDay = weekday >= 1 && weekday <= 5;

  // 交易时段判断
  const morningStart = 9 * 60 + 30; // 9:30
  const morningEnd = 11 * 60 + 30; // 11:30
  const afternoonStart = 13 * 60; // 13:00
  const afternoonEnd = 15 * 60; // 15:00

  let sessionType: TradingSessionInfo['sessionType'] = 'closed';
  let isTradingHours = false;

  if (isTradingDay) {
    if (currentMinutes < morningStart) {
      sessionType = 'pre_market';
    } else if (currentMinutes <= morningEnd) {
      sessionType = 'morning';
      isTradingHours = true;
    } else if (currentMinutes < afternoonStart) {
      sessionType = 'lunch_break';
    } else if (currentMinutes <= afternoonEnd) {
      sessionType = 'afternoon';
      isTradingHours = true;
    } else {
      sessionType = 'after_hours';
    }
  }

  const statusTextMap: Record<string, string> = {
    pre_market: '盘前',
    morning: '交易中',
    lunch_break: '午休中',
    afternoon: '交易中',
    after_hours: '已收盘',
    closed: '休市',
  };

  // 计算最近交易日和数据日期
  const currentDate = formatLocalDate(now);
  let lastTradingDay = currentDate;
  let dataDate = currentDate;
  
  if (!isTradingDay) {
    // 非交易日：往前找最近交易日
    const tempDate = new Date(now);
    for (let i = 0; i < 7; i++) {
      tempDate.setDate(tempDate.getDate() - 1);
      if (tempDate.getDay() >= 1 && tempDate.getDay() <= 5) {
        lastTradingDay = formatLocalDate(tempDate);
        dataDate = lastTradingDay;
        break;
      }
    }
  } else if (sessionType === 'pre_market') {
    // 盘前：使用前一交易日数据
    const tempDate = new Date(now);
    for (let i = 0; i < 7; i++) {
      tempDate.setDate(tempDate.getDate() - 1);
      if (tempDate.getDay() >= 1 && tempDate.getDay() <= 5) {
        dataDate = formatLocalDate(tempDate);
        break;
      }
    }
  }
  
  return {
    isTradingDay,
    isTradingHours,
    sessionType,
    statusText: statusTextMap[sessionType],
    currentDate,
    currentTime: now.toTimeString().split(' ')[0],
    nextOpen: null,
    refreshInterval: isTradingHours ? 5000 : 30000,
    cacheTTL: isTradingHours ? 5 : 60,
    lastTradingDay,
    dataDate,
  };
}
